import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import CustomModal from '../../custom/CustomModal';
import { CustomSelect } from '../../custom/CustomSelect';
import {
  extractFormValues,
  isNotNullValue,
  isNullValue,
  validateForm
} from '../../../utils/formUtil';
import {
  AvailableBox,
  OrderBox,
  SupplierBox
} from '../../ui/styled-views';
import API from '../../../data/api';
import { fNumber } from '../../../utils/formatNumber';
import palette from '../../../theme/palette';
import CustomTextField from 'src/components/custom/CustomTextField';

const initAssignment = {
  depot: { value: "", required: true, error: false, errorMessage: 'Please select a depot' },
  tank: { value: "", required: true, error: false, errorMessage: 'Please assign a tank' },
  note: { value: "", required: false, error: false, },
};

const OrderDepotTankTransfer = ({
  orderData,
  depots = [],
  openModal,
  handleCloseModal,
  handleSubmitCallback
}) => {

  const [submitted, setSubmitted] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [remainingVolume, setRemainingVolume] = useState(0);
  const [allocatedVolume, setAllocatedVolume] = useState(0);
  const [tanks, setTanks] = useState([]);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {

    setAssignment({ ...initAssignment });
    setAllocatedVolume(orderData?.allocated_volume || 0);
    setRemainingVolume(orderData?.available_volume || 0);
    setOrderId(orderData?.suborder_id || orderData?.order_id);

  }, [openModal, orderData, depots]);

  const getDepotTanks = async (depot_id, product_id, min_volume) => {
    if (isNullValue(depot_id) || isNullValue(product_id) || isNullValue(min_volume)) {
      console.log("getDepotTanks: Opps => ", depot_id, product_id, min_volume)
      return;
    }

    try {
      let endPoint = `inventory/logistics/tanks?depot_id=${depot_id}`
        .concat(`&product_id=${product_id}`)
        .concat(`&min_volume=${min_volume}`);

      const response = await API.get(endPoint);
      if (response.status === 200) {
        setTanks(response.data);
      } else {
        console.error('Error fetching depot tanks: ', response.status);
      }
    } catch (error) {
      console.error('Error fetching depot tanks: ', error);
    }
  };

  const handleSubmitAssignment = async () => {

    // If any of the inputs or required parameters is empty, return
    if (!validateForm(setAssignment, assignment) || isNullValue(orderId)) {

      console.log("Opps validation error")
      return;
    }

    const { depot, tank, note } = extractFormValues(assignment);

    setSubmitted(true);
    try {
      const response = await API.post(`inventory/logistics/assign_suborder_to_depot`, {
        client_suborder_id: orderId,
        depot_id: depot,
        tank_id: tank,
        product_id: orderData?.product_id,
        volume: orderData?.volume,
        note: note || null,
      });

      if (response.status === 200 || response.status === 206) {
        handleSubmitCallback();
      } else {
        console.error('Error creating suborder assignment: ', response.status);
      }
    } catch (error) {
      console.error('Error creating suborder assignment: ', error);
    }
    setSubmitted(false);
  };

  const handleResetAndClose = () => {
    setTanks([]);
    handleCloseModal();
  }

  const handleInputChange = (field, value, error = false) => {
    const _assignment = {
      ...assignment,
      [field]: {
        ...assignment[field],
        value: value,
        error: (isNullValue(value) && assignment[field]['required'] )|| error,
      },
    }
    setAssignment(_assignment);
  };

  const handleDepotAssignment = (depotId) => {

    getDepotTanks(depotId, orderData?.product_id, orderData?.available_volume || orderData?.volume);

    handleInputChange('depot', depotId, isNullValue(depotId))
  };

  return (
    <Paper>
      <CustomModal
        openModal={openModal}
        handleCloseModal={handleResetAndClose}
        modalTitle={`Assign Order ${orderId} to Depot Tank`}
      >
        <>
          <Typography variant="inherit" sx={{ color: palette.grey[500], my: 2  }}>
            {`${(isNotNullValue(orderData) && !orderData?.from_depot) ? orderData?.transporter_name : ''} `}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 2 }}>
            <Card sx={{ minWidth: 100, mx: 1, backgroundColor: 'orange', color: 'white' }}>
              <CardContent>
                <Typography variant="h6"> {`Truck Volume:`} </Typography>
                <Typography variant="body">{orderData?.volume}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 100, mx: 1, backgroundColor: 'lightblue', color: 'white' }}>
              <CardContent>
                <Typography variant="h6"> Company Name </Typography>
                <Typography variant="body">{orderData?.company_name}</Typography>

              </CardContent>
            </Card>
            <Card sx={{ minWidth: 100, mx: 1, backgroundColor: 'purple', color: 'white' }}>
              <CardContent>
                <Typography variant="h6"> Allocated Volume:  </Typography>
                <Typography variant="body">{fNumber(allocatedVolume)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 100, mx: 1, backgroundColor: 'black', color: 'white' }}>
              <CardContent>
                <Typography variant="h6">  Remaining Volume: </Typography>
                <Typography variant="body"> {fNumber(remainingVolume)} </Typography>
              </CardContent>
            </Card>
          </Box>

          <FormControl fullWidth sx={{ my: 2 }}>
            <CustomSelect
              labelId="depot-select-label"
              label={"Depot"}
              value={assignment?.depot.value}
              onChange={(e) => handleDepotAssignment(e.target.value)}
              required={assignment?.depot.required}
              error={assignment?.depot.error}
              helperText={assignment?.depot.error && assignment?.depot.errorMessage}
              // disabled={isNotNullValue(orderData?.supplier_id)}
            >
              <MenuItem value="" disabled>
                <em>Select Depot</em>
              </MenuItem>
              {depots?.map((d) => (
                <MenuItem key={"depot-order-" + d.depot_id}
                  value={d.depot_id}
                >
                  {d.depot_name}
                </MenuItem>
              ))}
            </CustomSelect>
          </FormControl>

          <FormControl fullWidth sx={{ my: 2 }}>
            <CustomSelect
              labelId="tank-select-label"
              label={"Tank"}
              value={assignment?.tank.value}
              onChange={(e) => handleInputChange('tank', e.target.value)}
              required={assignment?.tank.required}
              error={assignment?.tank.error}
              helperText={assignment?.tank.error && assignment?.tank.errorMessage}
              disabled={tanks?.length < 1}
            >
              <MenuItem value="" disabled>
                <em>Select Tank</em>
              </MenuItem>
              {tanks?.map((t) => (
                <MenuItem key={"tank-order-" + t.depot_id}
                  value={t.depot_id}
                >
                  <SupplierBox title="Tank Label" minWidth={250}>{t.tank_label}</SupplierBox>
                  <AvailableBox title="Availble Capacity"> {fNumber(t.available_volume)}</AvailableBox>
                  {/* <AllocatedBox title="Allocated Volume">{fNumber(client.allocated_volume)}</AllocatedBox> */}
                  <OrderBox title="Product">{t.product_name}</OrderBox>
                </MenuItem>
              ))}
            </CustomSelect>
          </FormControl>

          <CustomTextField sx={{ my: 1 }}
            fullWidth
            id="note"
            variant="outlined"
            name='note'
            label={`Note`}
            onChange={(e) => handleInputChange("note", e.target.value)}
            value={assignment?.note.value}
            required={assignment?.note.required}
            error={assignment?.note.error}
            helperText={assignment?.note.error && assignment?.note.errorMessage}
          />
        
          <Box sx={{ display: 'flex', justifyContent: 'right', mt: 2 }}>
            <Button
              variant='contained'
              color="inherit"
              onClick={handleResetAndClose}
            >
              Cancel
            </Button>
            <LoadingButton
              sx={{ ml: 2, }}
              variant="contained"
              loading={submitted}
              onClick={handleSubmitAssignment}
              // disabled={isRemainingVolumeZero}
            >
              Submit
            </LoadingButton>
          </Box>
        </>
      </CustomModal>
    </Paper>
  )
}

OrderDepotTankTransfer.propTypes = {

  /** Selected orderData details */
  orderData: PropTypes.object.isRequired,

  /** List of suitable depots */
  depota: PropTypes.array.isRequired,

  /** Flag to toggling openning of modal */
  openModal: PropTypes.bool,

  /** Callback function when closing modal */
  handleCloseModal: PropTypes.func.isRequired,

  /** Callback function after assignment of suborder */
  handleSubmitCallback: PropTypes.func.isRequired,

}

export default OrderDepotTankTransfer
